import type { UseQueryOptions, UseQueryResult } from '@tanstack/react-query'
import { useQuery, useQueryClient } from '@tanstack/react-query'

import {
  GET_AGGREGATE_SUGGESTED_BIDS_API_ID,
  getAggregateSuggestedBids,
} from '@/features/bidding/endpoints/aggregateSuggestedBids'
import { useErrorHandler } from '@/features/bidding/hooks/useErrorHandler'
import { buildAggregateSuggestedBidsResponse } from '@/features/bidding/mocks/aggregateSuggestedBids'
import type { AggregateSuggestedBidResponse } from '@/features/bidding/types/aggregateSuggestedBid'
import type { AggregateMarket } from '@/features/bidding/types/aggregateSuggestedBid'
import type { MarketDate } from '@/features/bidding/utils/date/marketDate'

export type UseAggregateSuggestedBidsQueryParams = {
  deliveryDay: MarketDate
  portfolioCode: string
  market: AggregateMarket | null
  resourceCategory: string
}

export type UseAggregateSuggestedBidsQueryResult = Omit<UseQueryResult, 'data'> & {
  aggregateSuggestedBids: AggregateSuggestedBidResponse | null
}

export function useAggregateSuggestedBidsQuery(
  params: UseAggregateSuggestedBidsQueryParams,
  options?: Omit<UseQueryOptions<AggregateSuggestedBidResponse>, 'queryKey' | 'queryFn'> & { useMockData?: boolean },
): UseAggregateSuggestedBidsQueryResult {
  const { deliveryDay, portfolioCode, market, resourceCategory } = params
  const useMockData = options?.useMockData ?? false

  const { data = null, ...queryResult } = useQuery({
    ...options,
    enabled: market !== null && (options?.enabled ?? true),
    queryKey: [GET_AGGREGATE_SUGGESTED_BIDS_API_ID, deliveryDay.toISODate(), portfolioCode, market, resourceCategory],
    queryFn: () =>
      useMockData
        ? Promise.resolve(buildAggregateSuggestedBidsResponse(deliveryDay))
        : getAggregateSuggestedBids({
            deliveryDay: deliveryDay.toISODate(),
            portfolioCode,
            market: market!,
            resourceCategory,
          }),
  })

  useErrorHandler(queryResult.isError)

  return { aggregateSuggestedBids: data, ...queryResult }
}

export function useInvalidateAggregateSuggestedBidsQuery() {
  const queryClient = useQueryClient()
  return () => queryClient.invalidateQueries({ queryKey: [GET_AGGREGATE_SUGGESTED_BIDS_API_ID] })
}
